import { Grid, Space, Typography } from '@arco-design/web-react';
import {
	AdvancedType,
	BlockManager,
	CUSTOM_LISTING_BLOCK_TYPE,
	IBlockData,
	t,
} from '@bay-email/core';
import {
	BlockAvatarWrapper,
	ReactIcon,
	useBlock,
	useEditorProps,
} from '@bay-email/editor';
import React, { useState } from 'react';
import { BsChevronUp, BsChevronDown, BsHouse } from 'react-icons/bs';
import { getIconByBlockType } from '@extensions/utils/getIconByBlockType';
import BayPillToggle from '@bay-ui/BayPillToggle';
import BayTooltip from '@bay-ui/BayTooltip';
import styles from './index.module.scss';
import { useExtensionProps } from '@extensions/components/Providers/ExtensionProvider';
import { BlockLibrary } from './BlockLibrary';

type BlockSource = 'system' | 'library';

export function Blocks() {
	const { categories } = useExtensionProps();
	const { blockLibrary, blockMode } = useEditorProps();
	const [source, setSource] = useState<BlockSource>('system');

	// The toggle appears only where there is a library to browse. Not in the
	// block editor itself: a reusable block is not a container for other saved
	// blocks, so offering the tab there would promise an insert we don't support.
	const showSourceToggle = Boolean(blockLibrary) && !blockMode;

	return (
		<div>
			{showSourceToggle && (
				<div className='mx-3 pt-3'>
					<BayPillToggle<BlockSource>
						ariaLabel={t('Block source')}
						variant='neutral'
						shape='square'
						size='md'
						fullWidth
						options={[
							{ value: 'system', label: t('System') },
							{ value: 'library', label: t('Block Library') },
						]}
						value={source}
						onChange={setSource}
					/>
				</div>
			)}

			{showSourceToggle && source === 'library' ? (
				<BlockLibrary />
			) : (
				<SystemBlocks categories={categories} />
			)}
		</div>
	);
}

function SystemBlocks({
	categories,
}: {
	categories: ReturnType<typeof useExtensionProps>['categories'];
}) {
	return (
		<div>
			{categories.map((cat, index) => {
				if (cat.displayType === 'column') {
					return (
						<LayoutCategory
							key={index}
							label={cat.label}
							blocks={cat.blocks as LayoutBlock[]}
						/>
					);
				}

				if (cat.displayType === 'custom') {
					return (
						<div key={index}>
							<h2 className='fs-5 p-3 m-0'>{cat.label}</h2>
							<Grid.Row>
								{cat.blocks.map((item, index) => {
									return <React.Fragment key={index}>{item}</React.Fragment>;
								})}
							</Grid.Row>
						</div>
					);
				}
				return (
					<div key={index}>
						<h2 className='fs-5 p-3 m-0'>{cat.label}</h2>
						<div className={styles.blockGrid}>
							{cat.blocks.map((item, index) => {
								return (
									<BlockItem
										key={index}
										{...(item as any)}
									/>
								);
							})}
						</div>
					</div>
				);
			})}
			</div>
	);
}

function BlockItem({
	type,
	payload,
	title,
	filterType,
}: {
	type: string;
	payload?: Partial<IBlockData>;
	title?: string;
	filterType: string | undefined;
}) {
	const block = BlockManager.getBlockByType(type);
	const { blockMode } = useEditorProps();
	const { values } = useBlock();

	// Block editor: the root holds at most one plain Wrapper and one Custom
	// Listing Wrapper. Once a wrapper is on the canvas its tile is spent — grey it
	// out so the only way to a second is to remove the first.
	const isWrapperType =
		type === AdvancedType.WRAPPER || type === CUSTOM_LISTING_BLOCK_TYPE;
	const alreadyPlaced =
		blockMode &&
		isWrapperType &&
		((values?.content?.children as IBlockData[] | undefined) || []).some(
			child => child.type === type,
		);

	const inner = (
		<div className={styles.blockItemContainer}>
			<ReactIcon
				IconComponent={getIconByBlockType(type, payload as any)}
				style={{ fontSize: 28 }}
				className='text-700'
			/>
			<Typography.Text
				// Arco sets word-break: break-all on .arco-typography, which splits
				// two-word names mid-word ("Listing Wra / pper"). Inline so it beats
				// that rule; break at the space, and only chop a single word that
				// genuinely can't fit the tile.
				style={{
					marginTop: 10,
					wordBreak: 'normal',
					overflowWrap: 'break-word',
				}}
				className='text-900'
			>
				{title || block?.name}
			</Typography.Text>
		</div>
	);

	if (alreadyPlaced) {
		return (
			<div className={styles.blockItem}>
				<BayTooltip
					placement='top'
					overlay={t('This block already has this wrapper. Remove it to add another.')}
				>
					<div
						style={{ opacity: 0.4, cursor: 'not-allowed' }}
						aria-disabled
					>
						{inner}
					</div>
				</BayTooltip>
			</div>
		);
	}

	return (
		<div className={styles.blockItem}>
			<BlockAvatarWrapper
				type={type}
				payload={payload}
			>
				{inner}
			</BlockAvatarWrapper>
		</div>
	);
}

// A "Listing" section is an ordinary section whose columns each own a listing,
// so dropping one into a Listing Wrapper gives a row of different listings. The
// choice is made here, at drag time, rather than per column after the fact.
export type SectionVariant = 'standard' | 'listing';

type LayoutBlock = { title?: string; payload: string[][] };

function LayoutCategory({ label, blocks }: { label: string; blocks: LayoutBlock[] }) {
	const [variant, setVariant] = useState<SectionVariant>('standard');

	return (
		<div className='mx-3'>
			<h2 className='fs-5 m-0 py-3'>{label}</h2>
			<BayPillToggle<SectionVariant>
				ariaLabel={label}
				className='mb-3'
				variant='neutral'
				size='md'
				fullWidth
				options={[
					{ value: 'standard', label: t('Standard') },
					{ value: 'listing', label: t('Listing') },
				]}
				value={variant}
				onChange={setVariant}
			/>
			{blocks.map((item, itemIndex) => (
				<LayoutItem
					key={item.title}
					title={item.title || ''}
					columns={item.payload}
					variant={variant}
					isLastRecord={itemIndex === blocks.length - 1}
				/>
			))}

			<Space direction='vertical'>
				<div />
			</Space>
		</div>
	);
}

function LayoutItem({
	columns,
	title,
	isLastRecord,
	variant,
}: {
	columns: string[][];
	title: string;
	isLastRecord: boolean;
	variant: SectionVariant;
}) {
	const [visible, setVisible] = useState(false);

	return (
		<div className='pb-2'>
			<p
				className='mb-2'
				role='button'
				onClick={() => setVisible(v => !v)}
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					cursor: 'pointer',
				}}
			>
				<span className='fw-bold text-capitalize text-900'>{title}</span>
				{columns.length > 1 && (
					<span>{!visible ? <BsChevronDown size='1rem' /> : <BsChevronUp size='1rem' />}</span>
				)}
			</p>
			{columns.map((item, index) => {
				const hide = !visible && index !== 0;
				const payload = {
					type: AdvancedType.SECTION,
					attributes: {},
					children: item.map(col => ({
						type: AdvancedType.COLUMN,
						attributes: {
							width: col,
						},
						data: {
							value:
								variant === 'listing'
									? {
											// Each column owns its own listing; the picker for it
											// shows up in the column's Listing Settings.
											isDynamicListing: true,
											selectedListingId: '',
											selectedListing: null,
										}
									: {},
						},
						children: [],
					})),
				};

				return (
					<div
						className='bg-white'
						key={index}
						style={{
							height: hide ? 0 : undefined,
							overflow: 'hidden',
							marginBottom: hide || isLastRecord ? 0 : '0.5rem',
						}}
					>
						<BlockAvatarWrapper
							type={AdvancedType.SECTION}
							payload={payload}
						>
							<div
								style={{
									border: '1px solid rgb(229, 229, 229)',
									width: '100%',
									padding: 10,
								}}
							>
								<div
									style={{
										height: 24,
										border: '1px solid rgb(85, 85, 85)',
										borderRadius: 3,
										display: 'flex',
									}}
								>
									{item.map((column, index) => {
										return (
											<div
												key={index}
												style={{
													borderRight:
														index === item.length - 1
															? undefined
															: '1px solid rgb(85, 85, 85)',
													height: '100%',
													width: column,
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													fontSize: '14px',
													color: 'rgb(85, 85, 85)',
													fontWeight: '500',
													gap: 4,
												}}
											>
												{/* Listing sections carry a listing per column — the
												    house marks which columns those are. */}
												{variant === 'listing' && (
													<BsHouse size={12} style={{ flexShrink: 0 }} />
												)}
												{column}
											</div>
										);
									})}
								</div>
							</div>
						</BlockAvatarWrapper>
					</div>
				);
			})}
		</div>
	);
}
